import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';

/**
 * Attaches the Keycloak access token as a `Bearer` header to same-origin API
 * calls (`/api/...`). The token is refreshed if near expiry by
 * `AuthService.getAccessToken()`. Requests for static assets or third-party
 * URLs pass through untouched.
 *
 * A `401` from the API means the session is gone on the server side (revoked,
 * or expired between refreshes) even when the local token still looked fine,
 * so it is handed to `AuthService.handleSessionExpired()` before the error
 * propagates to the caller. (Anonymous visitors never had a session, so the
 * hand-off ignores their 401s.)
 *
 * Once the user signed out in another tab of this browser
 * (`AuthService.signedOutElsewhere`, issue #171) the token this tab held is dead
 * and the request goes out WITHOUT one — not blocked. The portal has public
 * pages, and that is what this tab is now: an anonymous page, whose calls to the
 * public endpoints (address, master data, notifications) must keep working just
 * as any visitor's do; a protected endpoint answers 401 like it does for any
 * anonymous caller, and the hand-off already ran when the logout arrived.
 * (Permit, where every route needs a session, fails such requests outright
 * with a synthetic 401 instead — a deliberate difference, not an omission.)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith('/api')) {
    return next(req);
  }

  const auth = inject(AuthService);
  if (auth.signedOutElsewhere()) {
    return next(req);
  }

  return from(auth.getAccessToken()).pipe(
    switchMap((token) => {
      if (!token) {
        return next(req);
      }
      return next(
        req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }),
      );
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
        void auth.handleSessionExpired();
      }
      return throwError(() => error);
    }),
  );
};
